import { useEffect } from "react";

import {
  QueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { find, isEmpty } from "lodash/fp";

import {
  getUserEventGroups,
  patchUserEventGroup,
} from "@app/analytics/api/eventGroups";
import type {
  UserEventGroup,
  UserEventGroupPatchRequest,
} from "@app/analytics/types/eventGroupTypes";
import useAuth from "@app/common/auth/useAuth";
import { showToast } from "@app/common/hooks/useToasts";
import * as Arrays from "@app/utils/arrays";

const EMPTY_EVENTS: UserEventGroup[] = [];

type UseEventGroupOptions = QueryOptions<UserEventGroup[]> & {
  refetchInterval?: number;
  enabled?: boolean;
};

const useEventGroups = (
  { enabled = true, ...options }: UseEventGroupOptions = { enabled: true }
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["userEventGroups", user?.id];

  const { data, isError, isLoading } = useQuery({
    queryKey,
    queryFn: getUserEventGroups,
    enabled: Boolean(user?.id) && enabled,
    refetchInterval: options.refetchInterval,
  });

  useEffect(() => {
    if (isError) {
      showToast({
        message:
          "There was a problem loading your calendar events. Refresh the page and try again.",
        severity: "error",
      });
    }
  }, [isError]);

  const updateEventMutation = useMutation({
    mutationFn: (request: UserEventGroupPatchRequest) => {
      if (isEmpty(request.body)) return Promise.resolve();
      return patchUserEventGroup(request);
    },
    onMutate: async ({ id, body: patchBody }: UserEventGroupPatchRequest) => {
      if (isEmpty(patchBody)) return Promise.resolve();

      await queryClient.cancelQueries({ queryKey });
      const previousEvents =
        queryClient.getQueryData<UserEventGroup[]>(queryKey) || EMPTY_EVENTS;

      const existingEvent = find({ id }, previousEvents);

      if (!existingEvent) return;

      const combinedEvent = {
        ...existingEvent,
        ...patchBody,
      };

      const newQueryData = Arrays.replaceWhere(
        previousEvents,
        combinedEvent,
        (e) => e.id === id
      );

      queryClient.setQueryData(queryKey, newQueryData);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  return {
    isLoading,
    isError,
    events: data ?? EMPTY_EVENTS,

    updateEvent: updateEventMutation,
  };
};

export default useEventGroups;
